import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CardHeader,
  Divider,
  Grid,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  TextField,
  Tooltip,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { FileUploader } from "react-drag-drop-files";
import { object, string } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import makeStyles from "@material-ui/styles/makeStyles";
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from '@mui/icons-material/UploadFile';

import DefaultLogo from "../../../components/layout/DefaultLogo";
import FormInput from "../../../components/form/FormInput";
import { updateUserDetails, checkUsername } from "../../../redux/actions/auth";
import ConfirmModal from '../../../components/layout/ConfirmModal';
import config from "../../../config/config";
import "../../onboard/OnboardFormPage.style.css";

const profileSchema = object({
  firstName: string().min(1, 'First Name is required').max(30, 'First Name must be less than 30 letters'),
  lastName: string().min(1, 'Last Name is required').max(30, 'Last Name must be less than 30 letters'),
  username: string().min(1, 'User Name is required').max(30, 'User Name must be less than 30 letters'),
  email: string().min(1, 'Email is required').email('Email is invalid'),
  country: string(),
  city: string(),
  website: string().regex(
    /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
    'Invalid URL'
  ),
});

const FILE_TYPES = ["JPEG", "JPG", "PNG", "GIF"];
const containsText = (text, searchText) => text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export default function ProfileSettings({ user, updateUserAvatar, emailNotification }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    username: user?.username || "",
    email: user?.email || "",
    country: user?.country || "",
    city: user?.city || "",
    website: user?.website || "",
  };
  const methods = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues,
  });

  const [isUsernameDuplicated, setIsUsernameDuplicated] = useState(false);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(user?.country || "");
  const [searchText, setSearchText] = useState("");
  const [personalLogoFile, setPersonalLogoFile] = useState(null);
  const [personalLogoFileSelected, setPersonalLogoFileSelected] = useState(null);
  const [fileUploadError, setFileUploadError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const useStyles = makeStyles({
    menuItem: {
      color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
      fontSize: '13px',
      "&:hover": {
        color: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      },
    },
    menuItemSelected: {
      color: '#fff',
      backgroundColor: '#009ef7 !important',
      alignItems: 'center',
      justifyContent: 'space-between',
      "&:hover": {
        color: '#fff',
        backgroundColor: '#009ef7 !important',
      },
    },
  });
  const styles = useStyles();

  const getCountries = async () => {
    const { data } = await fetch(config.COUNTRY_URL).then(response => response.json());
    setCountries(data);
  }
  const displayedCountries = useMemo(
  () => countries.filter((option) => containsText(option.country, searchText)),
    [searchText, countries]
  );

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    methods.setValue("firstName", user?.firstName || "");
    methods.setValue("lastName", user?.lastName || "");
    methods.setValue("username", user?.username || "");
    methods.setValue("email", user?.email || "");
    methods.setValue("country", user?.country || "");
    methods.setValue("city", user?.city || "");
    methods.setValue("website", user?.website || "");
    setCountry(user?.country || "");
  }, [user]);

  const onChangeCountry = (e) => {
    setCountry(e.target.value);
    methods.setValue("country", e.target.value);
  }

  const onSubmitHandler = async (data) => {
    const res = await checkUsernameDuplicated();
    setIsUsernameDuplicated(res);
    if (res) {
      return;
    }
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  }

  const onCloseConfirmModal = () => {
    setConfirmModalVisible(false);
  }

  const handleChangePersonalLogo = (files) => {
    setFileUploadError('');
    setPersonalLogoFile(files[0]);
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onloadend = (e) => {
      setPersonalLogoFileSelected(reader.result);
    };
  };

  const onSaveChanges = async () => {
    onCloseModal();
    let formData = new FormData();
    formData.append('userId', user.id);
    formData.append('firstName', methods.getValues("firstName"));
    formData.append('lastName', methods.getValues("lastName"));
    formData.append('username', methods.getValues("username"));
    formData.append('email', methods.getValues("email"));
    formData.append('country', methods.getValues("country"));
    formData.append('city', methods.getValues("city"));
    formData.append('website', methods.getValues("website"));
    if (personalLogoFileSelected === 'default-logo') {
      formData.append('logo_remove', true); 
    } else {
      formData.append('logo', personalLogoFile);
    }
    const res = await dispatch(updateUserDetails(formData));
    updateUserAvatar();
    if (res) {
      setConfirmModalVisible(true);
    }
  }

  const checkUsernameDuplicated = async () => {
    const username = methods.getValues("username");
    const res = await dispatch(checkUsername(username));
    const { duplicated } = res;
    return duplicated;
  }

  const handleLogoRemove = () => {
    setPersonalLogoFileSelected('default-logo');
  }

  return (
    <>
      <Grid>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>Profile Settings</Typography>
      </Grid>
      <Divider sx={{ mt: 2 }} />
      <FormProvider {...methods}>
        <Box
          display='flex'
          flexDirection='column'
          component='form'
          noValidate
          autoComplete='off'
          onSubmit={methods.handleSubmit(onSubmitHandler)}
        >
          <Grid container alignItems="center" sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Name</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Shows on your profile and emails.</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <Grid
                container
                justifyContent='space-between'
                spacing={2}
              >
                <Grid
                  item
                  xs={6}
                >
                  <FormInput
                    placeholder='First Name'
                    type='text'
                    name='firstName'
                    fullWidth
                    required
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                >
                  <FormInput
                    placeholder='Last Name'
                    type='text'
                    name='lastName'
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Username</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Your unique identifier used for your Clientl links.</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='Username'
                fullWidth
                type='username'
                name='username'
              />
              {isUsernameDuplicated && <FormHelperText>Username has been duplicated</FormHelperText>} 
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Email</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>For account access and booking notifications.</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='Email'
                fullWidth
                type='email'
                name='email'
                required
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Country</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Your current country</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <Select
                fullWidth
                inputProps={{
                  sx: {
                    color: theme.palette.mode === 'light' ?
                      country ? '#5e6278' : '#bdc0ca' :
                      country ? '#fff' : '#3c3e50',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    padding: '11.3px 20px',
                  }
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: '300px' } } }}
                displayEmpty={true}
                renderValue={value => value ? value : 'Select Country...'}
                sx={{
                  padding: 0,
                  '.MuiSelect-select': { paddingY: '11.3px' },
                  '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
                }}
                value={country}
                onChange={onChangeCountry}>
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Type to search..."
                    fullWidth
                    sx={{ margin: '5px 0px' }}
                    inputProps={{
                      style: {
                        paddingTop: '0px',
                        paddingBottom: '0px',
                      }
                    }}
                    InputProps={{
                      style: {
                        padding: '10px 9px',
                        fontSize: '12px',
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        // Prevents autoselecting item while typing (default Select behaviour)
                        e.stopPropagation();
                      }
                    }}
                  />
                </ListSubheader>
                {displayedCountries.map((row, index) => (
                  <MenuItem
                    key={index}
                    value={row.country}
                    selected={row.country === country}
                    classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                  >
                    {row.country}
                    {row.country === country && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>City</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Your current city</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='City'
                fullWidth
                type='text'
                name='city'
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Website</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>Website URL (if applicable)</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FormInput
                placeholder='Website'
                fullWidth
                type='text'
                name='website'
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Personalize Branding</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: '12px', maxWidth: '300px', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>You may upload your personal logo here (recommended size 1080x240)</Typography>
            </Grid>
            <Grid item xs md sx={{ mt: { xs: 1, md: 0 } }}>
              <FileUploader
                multiple={true}
                handleChange={handleChangePersonalLogo}
                name="file"
                types={FILE_TYPES}
                children={
                  <CardHeader
                    avatar={<UploadFileIcon sx={{ fontSize: '2.5rem', color: '#009EF7' }} />}
                    title="Drop logo here or click to upload."
                    titleTypographyProps={{ fontSize: '1rem', fontWeight: 600, color: fileUploadError ? 'tomato' : '#3F4254' }}
                    subheader={fileUploadError ? fileUploadError : "(jpg, png files accepted)"}
                    subheaderTypographyProps={{ fontWeight: 600, color: fileUploadError ? 'tomato' : '#A1A5B7', fontSize: '0.8rem' }}
                    sx={{
                      border: '1px',
                      borderStyle: 'dashed',
                      borderColor: fileUploadError ? 'red' : '#009EF7',
                      borderRadius: 1,
                      backgroundColor: theme.palette.mode === 'light' ? '#F1FAFF' : '#212E48',
                      '&:hover ': {
                        cursor: 'pointer'
                      }
                    }}
                  />
                }
                onTypeError={(err) => setFileUploadError(err)}
                onSizeError={(err) => console.log(err)}
              />
              <Box display="inline-flex" sx={{ boxShadow: 0, borderRadius: '6px', padding: '0px', mt: 1, position: 'relative', width: '95%' }}>
                {personalLogoFileSelected ?
                  personalLogoFileSelected === 'default-logo' ?
                    <DefaultLogo mode="dark" /> :
                    <Box
                      component="img"
                      src={personalLogoFileSelected}
                      sx={{ backgroundColor: '#222', width: '100%', borderRadius: '6px' }}
                      alt="Personal Logo"
                    /> :
                  emailNotification.companyLogo !== 'uploads/user-logo.png' ?
                    <Box
                      component="img"
                      src={personalLogoFileSelected ? personalLogoFileSelected :`${config.SERVER_BASE_URL}/${emailNotification.companyLogo}`}
                      sx={{ backgroundColor: '#222', width: '100%', borderRadius: '6px' }}
                      alt="Personal Logo"
                    /> :
                    <DefaultLogo mode="dark" />
                }
                <Tooltip title="Remove Logo" placement="top" enterTouchDelay={0}>
                  <span className="Image-remove" onClick={handleLogoRemove}>
                    <i className="fa fa-remove"></i>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2 }} />
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              onClick={() => navigate('/bookings')}
              size="large"
              sx={{
                backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '6px',
                padding: '11px 20px',
                "&:hover": {
                  backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212E48',
                  color: '#009ef7',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              sx={{ marginInlineStart: 1, color: '#fff', fontWeight: 600, textTransform: 'none' }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <ConfirmModal
        visible={modalVisible}
        onClose={onCloseModal}
        symbol="?"
        title="Apply changes?"
        description="Your profile will be updated with these new settings."
        titleYes="Confirm"
        titleNo="Cancel"
        onClickYes={onSaveChanges}
        onClickNo={onCloseModal}
      />
      <ConfirmModal
        visible={confirmModalVisible}
        onClose={onCloseConfirmModal}
        symbol="&#10004;"
        title="Success!"
        description="Your settings have been saved."
        titleNo="Continue"
        onClickNo={onCloseConfirmModal}
      />
    </>
  );
}
